import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ActivityButton } from 'components/ActivityButton'
import { usePostPatronBadgeReward } from 'hooks/api/Contests/BadgeReward/usePostPatronBadgeReward'
import { DataTable } from 'components/Shared/DataTable'
import GiftIcon from 'assets/gift_empty_state.svg'
import { useSnackbar } from 'stores/useSnackbar'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import {
  type BadgeRewardType,
  type BadgeReward,
  type BadgeRewardRenewableType,
} from 'types/api'
import { useEffect, useMemo, useState } from 'react'
import { useGetPatronBadgeRewardsByPatronId } from 'hooks/api/Contests/BadgeReward/useGetPatronBadgeRewardsByPatronId'
import { dataTablePromotionDateFormatter } from 'utils/util'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { useGetAllCorporateAccounts } from 'hooks/api/useGetAllCorporateAccounts'
import { useGetAllOrganizations } from 'hooks/api/useGetAllOrganizations'
import { useGetAllLocationBadges } from 'hooks/api/Contests/BadgeReward/useGetAllLocationBadges'
import { PatronBadgeProgressModal } from './PatronBadgeProgressModal'

const getColumns = (
  corporateAccountsMap: Map<number, string>,
  organizationsMap: Map<number, string>
): GridColDef[] => [
  { field: 'id', headerName: 'ID', width: 100 },
  {
    field: 'name',
    headerName: 'Name',
    width: 180,
  },
  {
    field: 'description',
    headerName: 'Description',
    width: 240,
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 240,
    valueGetter: (params) => {
      const badgeType = (typ: BadgeRewardType) => {
        switch (typ) {
          case 'LocationCheckIn':
            return 'Location Check-In'
          case 'LocationCheckInStreak':
            return 'Location Check-In Streak'
          case 'LocationUniqueCheckIn':
          case 'UniqueLocationVisits':
            return 'Location Unique Check Ins'
          default:
            return ''
        }
      }

      const badgeRewardType = params.row.type as BadgeRewardType
      return badgeType(badgeRewardType)
    },
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    width: 170,
    valueFormatter: dataTablePromotionDateFormatter(false, 'Start'),
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    width: 170,
    valueFormatter: dataTablePromotionDateFormatter(false, 'End'),
  },
  {
    field: 'renewableType',
    headerName: 'Renewable',
    width: 180,
    valueGetter: (params) => {
      const renewableType = (typ: BadgeRewardRenewableType) => {
        switch (typ) {
          case 'Daily':
            return 'Daily'
          case 'Weekly':
            return 'Weekly'
          case 'Monthly':
            return 'Monthly'
          case 'Yearly':
            return 'Yearly'
          case 'CheckInStreak':
            return 'Check-In Streak'
          default:
            return 'No'
        }
      }

      const leRenewableType = params.row
        .renewableType as BadgeRewardRenewableType
      return renewableType(leRenewableType)
    },
  },
  {
    field: 'dependentBadgeRewardId',
    headerName: 'Depends On Badge Id',
    width: 220,
    valueGetter: (params) => {
      return params.row.dependentBadgeRewardId === 0
        ? ''
        : params.row.dependentBadgeRewardId
    },
  },
  {
    field: 'locationPointsAwarded',
    headerName: 'Location Points to award',
    width: 240,
    valueGetter: (params) => {
      const pointsAwarded = params.row.locationPointsAwarded

      return pointsAwarded === 0 ? '' : pointsAwarded
    },
  },
  {
    field: 'subType',
    headerName: 'Locations',
    width: 240,
    valueGetter: (params) => {
      if (params.row.subtype === 'Location') {
        const badgeLEs = params.row.badgeLEs ?? []
        return badgeLEs.length === 1
          ? `${String(badgeLEs[0]?.licensedEstablishment?.standardName)}`
          : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `${badgeLEs.length} Location(s)`
      } else if (params.row.subtype === 'CorporateAccount') {
        return params.row.corporateAccountId
          ? (corporateAccountsMap.get(params.row.corporateAccountId) ?? '')
          : ''
      } else if (params.row.subtype === 'Organization') {
        return params.row.organizationId
          ? (organizationsMap.get(params.row.organizationId) ?? '')
          : ''
      }
      return ''
    },
  },
]

export const PatronAwardOrProgressBadgeModal = ({
  patronId,
  type,
  isModalOpen,
  toggleModalIsOpen,
}: {
  patronId: number
  type: 'Award' | 'Progress'
  isModalOpen: boolean
  toggleModalIsOpen: () => void
}) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '80%'
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const [selectedBadgeReward, setSelectedBadgeReward] =
    useState<BadgeReward | null>(null)
  const [filteredData, setFilteredData] = useState<BadgeReward[] | null>(null)

  const [isBadgeProgressModalOpen, setIsBadgeProgressModalOpen] =
    useState(false)

  const toggleBadgeProgressModalIsOpen = () => {
    setIsBadgeProgressModalOpen(!isBadgeProgressModalOpen)
  }

  useEffect(() => {
    if (isModalOpen) {
      setSelectedBadgeReward(null)
    }
  }, [isModalOpen])

  const badgeRewardsQuery = useGetAllLocationBadges()

  const patronBadgeRewardsQuery = useGetPatronBadgeRewardsByPatronId({
    patronId,
  })

  const locationBadgesQuery = useGetAllLocationBadges()

  const corporateAccountsQuery = useGetAllCorporateAccounts()

  const organizationsQuery = useGetAllOrganizations()

  const corporateAccountsMap = useMemo(() => {
    const map = new Map<number, string>()
    corporateAccountsQuery.data?.forEach((ca) => {
      if (ca.id && ca.name) map.set(ca.id, ca.name)
    })
    return map
  }, [corporateAccountsQuery.data])

  const organizationsMap = useMemo(() => {
    const map = new Map<number, string>()
    organizationsQuery.data?.forEach((org) => {
      if (org.id && org.name) map.set(org.id, org.name)
    })
    return map
  }, [organizationsQuery.data])

  const locationBadgesMap = useMemo(() => {
    const map = new Map<number, BadgeReward>()
    locationBadgesQuery.data?.forEach((badge) => {
      if (badge.id) map.set(badge.id, badge as unknown as BadgeReward)
    })
    return map
  }, [locationBadgesQuery.data])

  const columns = useMemo(
    () => getColumns(corporateAccountsMap, organizationsMap),
    [corporateAccountsMap, organizationsMap]
  )

  const usePostPatronBadgeRewardMutation = usePostPatronBadgeReward({
    patronId,
    badgeRewardId: selectedBadgeReward?.id ?? 0,
    onSuccess: async () => {
      setSnackbarMessage(`Successfully awarded badge to patron`, 'success')
      toggleModalIsOpen()
    },
    onError: () =>
      setSnackbarMessage(`Error: Cannot save Patron badge reward`, 'error'),
  })

  if (badgeRewardsQuery.isPending || patronBadgeRewardsQuery.isPending) {
    return <CircularProgress />
  }

  if (badgeRewardsQuery.isError || patronBadgeRewardsQuery.isError) {
    return <p>An error occured.</p>
  }

  const handleSave = () => {
    if (!usePostPatronBadgeRewardMutation.isPending) {
      usePostPatronBadgeRewardMutation.mutate({
        patronId,
        badgeRewardId: selectedBadgeReward?.id ?? 0,
      })
    }
  }

  const handleFilteredData = (filteredData: BadgeReward[]) => {
    setSelectedBadgeReward(null)
    setFilteredData(filteredData)
  }

  const selectRow: GridEventListener<'rowClick'> = (params) => {
    if (params.id !== null) {
      const selectedBadge =
        badgeRewardsQuery.data.find((br) => br?.id === params.row.id) ?? null
      setSelectedBadgeReward(selectedBadge)
    }
  }

  const locationBadgeRewards = Array.from(
    new Map(
      (badgeRewardsQuery.data ?? [])
        .filter(
          (x) =>
            x?.type === 'LocationCheckIn' ||
            x?.type === 'LocationCheckInStreak' ||
            x?.type === 'LocationUniqueCheckIn' ||
            x?.type === 'UniqueLocationVisits'
        )
        .map((badgeReward) => [
          badgeReward?.id,
          badgeReward?.id
            ? {
                ...badgeReward,
                ...(locationBadgesMap.get(badgeReward.id) ?? {}),
              }
            : badgeReward,
        ])
    ).values()
  )

  const rows = locationBadgeRewards.filter(
    (badgeReward) =>
      !patronBadgeRewardsQuery.data.some(
        (patronBadgeReward) =>
          patronBadgeReward.badgeRewardId === badgeReward.id
      )
  )

  const onModalClose = () => {
    toggleModalIsOpen()
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '65%',
        height: heightModal,
      }}
    >
      <Box>
        <ModalFullHeader
          toggleIsOpen={toggleModalIsOpen}
          title={type === 'Award' ? 'Select Badge Reward' : 'Badge Progress'}
        />
        <ModalFullBody
          heightModal={heightModal}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <form>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              {selectedBadgeReward?.name && (
                <>
                  <Typography component="span">Selected:</Typography>
                  <Typography component="span" sx={{ color: 'primary.main' }}>
                    {selectedBadgeReward.name}
                  </Typography>
                </>
              )}
              {type === 'Award' ? (
                <ActivityButton
                  active={usePostPatronBadgeRewardMutation.isPending}
                  type="button"
                  variant="contained"
                  disabled={!selectedBadgeReward?.id}
                  onClick={handleSave}
                >
                  Award Badge to Patron
                </ActivityButton>
              ) : (
                <ActivityButton
                  active={false}
                  type="button"
                  variant="contained"
                  disabled={!selectedBadgeReward?.id}
                  onClick={toggleBadgeProgressModalIsOpen}
                >
                  Show Progress
                </ActivityButton>
              )}
            </div>
          </form>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <GridSearchField<BadgeReward>
              placeholder="Search Badge Rewards"
              data={rows}
              handleFilteredData={handleFilteredData}
              fullWidth
            />
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: 'scroll',
              }}
            >
              <DataTable
                data-testid="patron-award-progress-badge-rewards-table"
                columns={columns}
                rows={filteredData ?? rows}
                autoHeight={false}
                sx={{ height: '100%', minWidth: 'max-content' }}
                noDataMessage="No Badges found."
                emptyDataImageSource={GiftIcon}
                forceDesktopTableInMobile
                onRowClick={selectRow}
                rowSelectionModel={
                  selectedBadgeReward ? [Number(selectedBadgeReward.id)] : []
                }
              />
            </Box>
          </Box>
        </ModalFullBody>
        <ModalFullFooter>
          <Button onClick={() => onModalClose()}>Close</Button>
        </ModalFullFooter>
      </Box>

      <PatronBadgeProgressModal
        patronId={Number(patronId)}
        selectedBadgeReward={selectedBadgeReward}
        isModalOpen={isBadgeProgressModalOpen}
        toggleModalIsOpen={toggleBadgeProgressModalIsOpen}
      />
    </Modal>
  )
}
